import { ENV } from '@/config/env';

/**
 * Thin fetch wrapper for the V8 backend. Always sends credentials:
 * 'include' so the httpOnly session cookie rides along — the frontend
 * never reads or stores the session token itself. Every non-2xx response
 * throws ApiError with the parsed error body so callers can branch on
 * e.g. 401 -> redirect to /login.
 */
function extractErrorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const value = (body as { error: unknown }).error;
    if (typeof value === 'string') return value;
  }
  return `HTTP ${status}`;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(extractErrorMessage(body, status));
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const base = ENV.apiBaseUrl.replace(/\/+$/, '');
  const normalizedPath =
    base.endsWith('/api') && path.startsWith('/api/')
      ? path.slice(4)
      : path;
  const res = await fetch(`${base}${normalizedPath}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json() : undefined;
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
